import { ApiError } from '@/lib/api';
import type {
  ReviewCaseState,
  ReviewEvent,
  ReviewExtract,
  ReviewGrid,
  ReviewIneligibleReason,
  ReviewOperation,
  ReviewRefusalCode,
  ReviewStamp,
  ReviewValidation,
} from '@matter_extractor/shared';
import { reviewExtractSchema, reviewRefusalSchema } from '@matter_extractor/shared';
import { caseNumber, shortPassage } from './review-transcript';

/**
 * What the REVIEW page SAYS (relecture.md §3 to §10) - pure, the régime of
 * `session-labels.ts`: the screens draw, this module words.
 *
 * Every refusal is worded from its `code` and never from the server's message,
 * which is a developer's string (`ApiError`).
 */

/* ------------------------------------------------------------------- the dates */

const MONTHS_SHORT = [
  'janv.',
  'févr.',
  'mars',
  'avr.',
  'mai',
  'juin',
  'juil.',
  'août',
  'sept.',
  'oct.',
  'nov.',
  'déc.',
] as const;

function parts(iso: string): { date: Date; valid: boolean } {
  const date = new Date(iso);
  return { date, valid: !Number.isNaN(date.getTime()) };
}

const two = (value: number): string => String(value).padStart(2, '0');

/** « 10 oct. 09:12 » - the moment a stamp prints, in the reader's own time. */
export function reviewMoment(iso: string): string {
  const { date, valid } = parts(iso);
  if (!valid) return '';
  return `${date.getDate()} ${MONTHS_SHORT[date.getMonth()] ?? ''} ${two(date.getHours())}:${two(date.getMinutes())}`;
}

/** « 10 oct. » - the day, for the line of the frozen grid. */
export function reviewDay(iso: string): string {
  const { date, valid } = parts(iso);
  if (!valid) return '';
  return `${date.getDate()} ${MONTHS_SHORT[date.getMonth()] ?? ''}`;
}

/** « 9 oct. 2026 » - the day with its year, for the breadcrumb and the sub-title. */
export function reviewFullDay(iso: string): string {
  const { date, valid } = parts(iso);
  if (!valid) return '';
  return `${reviewDay(iso)} ${date.getFullYear()}`;
}

/* ------------------------------------------------------------------ the authors */

/**
 * « Patrick » for `patrick@exemple.fr`, « Alice Pesty » for `alice.pesty@…`:
 * the part before the `@`, its words capitalised. What is not an email - the
 * shared token's « jeton partagé » - is said as it is.
 */
export function authorLabel(by: string): string {
  const at = by.indexOf('@');
  if (at <= 0) return by;
  return by
    .slice(0, at)
    .split(/[._-]+/)
    .filter((word) => word !== '')
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(' ');
}

/** What a stamp prints: who, when, and for the assistant « via Claude » with its « d'après ». */
export interface StampLine {
  readonly who: string;
  readonly when: string;
  readonly viaClaude: boolean;
  readonly dapres: string | null;
}

export function stampLine(stamp: ReviewStamp): StampLine {
  return {
    who: authorLabel(stamp.by),
    when: reviewMoment(stamp.at),
    viaClaude: stamp.via === 'claude',
    dapres: stamp.dapres,
  };
}

/* ----------------------------------------------------------------------- cases */

/** « 4. Expérience » - the number and the name of a case, as the history and the popup say it. */
export function caseLabel(
  grid: Pick<ReviewGrid, 'objectives'> | null,
  objectiveId: string,
): string {
  const objectives = grid?.objectives ?? [];
  const at = objectives.findIndex((objective) => objective.objectiveId === objectiveId);
  const name = objectives[at]?.name;
  return at < 0 || name === undefined ? objectiveId : `${at + 1}. ${name}`;
}

/** The case numbers, in the order of the grid, of a set of extracts: « 1, 6 ». */
export function caseNumbers(order: readonly string[], extracts: readonly ReviewExtract[]): string {
  return [
    ...new Set(extracts.map((extract) => caseNumber(order, extract.objectiveId)).filter(Boolean)),
  ]
    .sort((a, b) => a - b)
    .join(', ');
}

export const CASE_STATE_LABEL: Readonly<Record<ReviewCaseState, string>> = {
  empty: 'vide',
  'to-validate': 'à valider',
  'to-revalidate': 'à revalider',
  valid: 'validée',
  invalid: 'non validée',
};

export type CaseStateTone = 'none' | 'ok' | 'ko' | 'todo';

export const CASE_STATE_TONE: Readonly<Record<ReviewCaseState, CaseStateTone>> = {
  empty: 'none',
  'to-validate': 'todo',
  'to-revalidate': 'todo',
  valid: 'ok',
  invalid: 'ko',
};

/** The validations of a case, the most recent first (§6). */
export function validationsOf(
  validations: readonly ReviewValidation[],
  objectiveId: string,
): ReviewValidation[] {
  return validations
    .filter((validation) => validation.objectiveId === objectiveId)
    .sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
}

/**
 * « validée · Patrick », « non validée · Nicolas », « à revalider »: the state
 * the server derived, with the author of the validation that gives it.
 */
export function caseStateLine(
  state: ReviewCaseState,
  validations: readonly ReviewValidation[],
  objectiveId: string,
): string {
  const label = CASE_STATE_LABEL[state];
  if (state !== 'valid' && state !== 'invalid') return label;
  const latest = validationsOf(validations, objectiveId).find((validation) => validation.current);
  return latest === undefined ? label : `${label} · ${authorLabel(latest.by)}`;
}

/* ------------------------------------------------------------------- the grid */

export const RULE_LABEL = {
  one: 'un passage, un seul objectif',
  two: 'un passage peut servir deux objectifs',
} as const;

/**
 * The line that heads the Rangement tab (§5.1): « Grille : les 6 cibles de
 * Scénario de succès v13, version jouée · figée le 10 oct. par Alice », or what
 * the first gesture will freeze while nothing is.
 */
export function gridLine(grid: ReviewGrid, protocolName: string | null): string {
  const count = grid.objectives.length;
  const targets = count === 1 ? 'la cible' : `les ${count} cibles`;
  const name = `${protocolName ?? 'du protocole'}`;
  const of = protocolName === null ? name : `de ${name}`;
  const origin = grid.source === 'played' ? 'version jouée' : 'version de développement';
  const version = `${targets} ${of} v${grid.version}, ${origin}`;
  if (!grid.frozen || grid.frozenAt === null) {
    return `La grille se figera à votre premier geste : ${version}.`;
  }
  const by = grid.frozenBy === null ? '' : ` par ${authorLabel(grid.frozenBy)}`;
  return `Grille : ${version} · figée le ${reviewDay(grid.frozenAt)}${by}`;
}

/* ------------------------------------------------------------------ eligibility */

/** What the page says instead of the review, or above it, when it cannot be written. */
export const INELIGIBLE_SENTENCE: Readonly<Record<ReviewIneligibleReason, string>> = {
  'session-in-progress': 'La relecture s’ouvre quand la session est finie.',
  'session-in-trash': 'La relecture se lit ; restaurez la session pour la modifier.',
  'no-protocol-page':
    'Cette expérience n’a pas de page protocole : il n’y a pas de conversation à relire.',
};

/* ---------------------------------------------------------------- the refusals */

export const REVIEW_REFUSAL_SENTENCE: Readonly<Record<ReviewRefusalCode, string>> = {
  'session-in-trash': 'Cette session est à la corbeille : restaurez-la pour modifier la relecture.',
  'session-in-progress': 'La relecture s’ouvre quand la session est finie.',
  'no-protocol-page': 'Cette expérience n’a pas de page protocole : il n’y a rien à relire.',
  'review-no-grid': 'Aucune cible de rangement : il n’y a pas de case où ranger.',
  'review-unknown-objective': 'Cette case n’est pas dans la grille de la relecture.',
  'review-unknown-element': 'Cet élément n’existe plus : la relecture vient d’être relue.',
  'review-not-verbatim': 'Ce passage n’est pas mot pour mot dans la réponse : rien n’a été rangé.',
  'review-turn-out-of-range': 'Ce tour n’a pas de réponse de la personne : rien n’a été rangé.',
  'review-doubt-locked':
    'Ce « ? » est verrouillé : retirez l’un des deux placements pour le lever.',
  'review-noyau-invalid':
    'Ce choix de noyau n’est pas cohérent : un second candidat suppose un doute et un autre noyau.',
  'review-empty-text': 'Écrivez la note avant de l’ajouter.',
};

export const REVIEW_FAILURE_SENTENCE = 'Le geste n’a pas pu être enregistré. Réessayez.';

/** The code of a refusal of the WHOLE request - a 409 whose body is the contract - or null. */
export function reviewRefusalOf(error: unknown): ReviewRefusalCode | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const refusal = reviewRefusalSchema.safeParse(error.details);
  return refusal.success ? refusal.data.code : null;
}

/** What the page says of a request that failed, from its code. */
export function reviewErrorSentence(error: unknown): string {
  const code = reviewRefusalOf(error);
  return code === null ? REVIEW_FAILURE_SENTENCE : REVIEW_REFUSAL_SENTENCE[code];
}

/* ----------------------------------------------------------------- the journal */

type Row = Readonly<Record<string, unknown>>;

function rowOf(value: unknown): Row | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Row)
    : null;
}

function text(row: Row | null, key: string): string | null {
  const value = row?.[key];
  return typeof value === 'string' ? value : null;
}

function count(row: Row | null, key: string): number | null {
  const value = row?.[key];
  return typeof value === 'number' ? value : null;
}

/** « t6 » and a short quote - what an extract of the journal says of itself. */
function extractWords(row: Row | null): string {
  const turn = count(row, 'turn');
  const quote = text(row, 'text');
  const tour = turn === null ? '' : `t${turn}`;
  const words = quote === null ? '' : `« ${shortPassage(quote, 40)} »`;
  return [tour, words].filter((part) => part !== '').join(' ');
}

/** The non-empty parts, joined by a space. */
function words(...parts: readonly (string | null)[]): string {
  return parts.filter((part) => part !== null && part !== '').join(' ');
}

/**
 * One line of the Historique tab (§10), in the third person - « range t6 « Trois
 * voisins… » en 4. Expérience » - read off the `before` and `after` rows the
 * journal keeps. A row that does not read says what it can: the operation and
 * nothing of its object, never an invented one.
 */
export function describeEvent(
  event: Pick<ReviewEvent, 'op' | 'before' | 'after'>,
  grid: Pick<ReviewGrid, 'objectives'> | null,
): string {
  const before = rowOf(event.before);
  const after = rowOf(event.after);
  const row = after ?? before;
  const objectiveId = text(row, 'objectiveId');
  const place = objectiveId === null ? '' : caseLabel(grid, objectiveId);
  const extract = extractWords(row);
  const turn = count(row, 'turn');
  switch (event.op) {
    case 'range':
      return words('range', extract, place === '' ? null : `en ${place}`);
    case 'unrange':
      return words('retire', extract, place === '' ? null : `de ${place}`);
    case 'set-doubt': {
      const up = after?.doubt === true;
      return words(
        up ? 'pose' : 'lève',
        'le « ? » sur',
        extract,
        place === '' ? null : `en ${place}`,
      );
    }
    case 'note-extract':
      return words('note l’extrait', extract);
    case 'note-case':
      return place === '' ? 'note une case' : `note la case ${place}`;
    case 'validate-case': {
      const verdict = text(after, 'verdict');
      const verb = verdict === 'invalid' ? 'ne valide pas' : 'valide';
      return place === '' ? `${verb} une case` : `${verb} la case ${place}`;
    }
    case 'annotate-question': {
      const verdict = text(after, 'verdict');
      const tour = turn === null ? '' : ` du tour ${turn}`;
      const how = verdict === 'bad' ? 'ne va pas' : verdict === 'good' ? 'va' : null;
      return `annote la question${tour}${how === null ? '' : ` : ${how}`}`;
    }
    case 'edit-question-note':
      return 'modifie une annotation';
    case 'remove-question-note':
      return 'retire une annotation';
    case 'set-noyau': {
      if (after?.unreadable === true) return 'dit : pas de noyau lisible';
      const code = text(after, 'noyau');
      if (code === null) return 'efface le noyau';
      const doubtful = text(after, 'certainty') === 'doubtful';
      const second = text(after, 'second');
      const doubt = doubtful ? ', avec doute' : '';
      const maybe = second === null ? '' : `, ou bien ${second}`;
      return `pose le noyau ${code}${doubt}${maybe}`;
    }
    case 'add-note':
      return 'ajoute une note sur la session';
    case 'edit-note':
      return 'modifie une note sur la session';
    case 'remove-note':
      return 'retire une note sur la session';
  }
}

/** The fields of an `unrange` event's row that « Remettre » sends back as a `range`. */
const restorableRow = reviewExtractSchema.pick({
  objectiveId: true,
  turn: true,
  start: true,
  end: true,
  text: true,
});

/**
 * « Remettre » (§10): the `range` that puts back what an `unrange` event
 * removed, rebuilt from the extract row the journal keeps in `before`. Null for
 * any other event, and for one whose row does not read.
 *
 * It is not an operation of its own: the server sees a gesture like any other
 * and checks the passage word for word again.
 */
export function restoreOperation(
  event: Pick<ReviewEvent, 'op' | 'before'>,
): Extract<ReviewOperation, { op: 'range' }> | null {
  if (event.op !== 'unrange') return null;
  const parsed = restorableRow.safeParse(event.before);
  return parsed.success ? { op: 'range', ...parsed.data } : null;
}

/** Is that passage already in its case - so « Remettre » has nothing left to put back? */
export function isRestored(
  operation: Extract<ReviewOperation, { op: 'range' }>,
  extracts: readonly ReviewExtract[],
): boolean {
  return extracts.some(
    (extract) =>
      extract.objectiveId === operation.objectiveId &&
      extract.turn === operation.turn &&
      extract.start === operation.start &&
      extract.end === operation.end,
  );
}
